import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, ButtonSize, ButtonType, Dropdown, Icon, IconSize, IDropdownItem, Input, Tag, Tags} from "@renta-apps/renta-react-components";
import Localizer from "@/localization/Localizer";
import styles from "./FiltersAndSort.module.scss";
import {useLocalStorage, useResize} from "@/helpers/Hooks";
import {searchProductGroupNames} from "@/services/ProductService";
import {ProductGroupName} from "@/models/server/ProductGroupName";
import {getMostPopularSearchesAsync, saveSearchLogAsync} from "@/services/SearchLogsService";
import {SearchType} from "@/models/Enums";
import {EquipmentsFiltersSortAndPagination} from "@/pages/ConstructionSiteDetails/EquipmentsNew/EquipmentsNew";
import RentaEasyConstants from "@/helpers/RentaEasyConstants";

interface IFiltersAndSortProps {
    onGetReportModalOpen: () => void;
    params: EquipmentsFiltersSortAndPagination;
    onParamValueChange: (change: Partial<EquipmentsFiltersSortAndPagination>) => void;
}

enum FilterType {
    DEVICE_NAMES = "device_names",
    PRODUCT_GROUPS = "product_groups",
    STATUSES = "statuses",
}

const FiltersAndSort: React.FC<IFiltersAndSortProps> = ({params, onParamValueChange, onGetReportModalOpen}) => {
    const [deviceName, setDeviceName] = useState<string>('');
    const [productGroup, setProductGroup] = useState<string>('');
    const [popularProductGroups, setPopularProductGroups] = useState<IDropdownItem[]>([]);
    const [deviceNameSuggestions, setDeviceNameSuggestions] = useLocalStorage<string[]>("renta.equipments.deviceName.suggestions", []);
    const [productGroupSuggestions, setProductGroupSuggestions] = useLocalStorage<string[]>("renta.equipments.productGroup.suggestions", []);
    const [combinedFilterValues, setCombinedFilterValues] = useState<Tag[]>([]);
    const [productGroupNames, setProductGroupNames] = useState<ProductGroupName[]>([]);
    const [additionalFiltersVisible, setAdditionalFiltersVisible] = useState(false);
    const {isMobile} = useResize(200);

    const hasTags = useMemo(() => combinedFilterValues.length > 0, [combinedFilterValues]);

    const statuses: IDropdownItem[] = useMemo(() => {
        return params.statuses.map(item => ({value: item.id, name: item.name || ''}));
    }, [params.statuses]);

    useEffect(() => {
        setCombinedFilterValues([
            ...params.deviceNames.map((item) => (
                {id: item, label: item ?? '', tooltip: Localizer.fleetMonitoringPageFiltersTooltipNameOrId, type: FilterType.DEVICE_NAMES}
            )),
            ...params.productGroups.map((item) => (
                {id: item.id ?? '', label: item.name ?? '', tooltip: Localizer.fleetMonitoringPageGridProductGroup, type: FilterType.PRODUCT_GROUPS}
            )),
            ...params.statuses.map((item) => (
                {id: item.id ?? '', label: item.name ?? '', tooltip: Localizer.constructionSiteDetailsEquipmentTabStatus, type: FilterType.STATUSES}
            )),
        ]);
    }, [params.deviceNames, params.productGroups, params.statuses]);

    useEffect(() => {
        if (productGroupSuggestions.length) {
            return;
        }

        loadPopularProductGroups().catch();
    }, [productGroupSuggestions]);

    const productGroupsSuggestionsDelayMs = useMemo(() => {
        return productGroup.length < 3 ? 0 : 300;
    }, [productGroup]);

    const sortOrderItems: IDropdownItem[] = [
        {name: Localizer.enumSortDirectionAsc, value: "Asc"},
        {name: Localizer.enumSortDirectionDesc, value: "Desc"},
    ];

    const sortByItems: IDropdownItem[] = [
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByName, value: "Name"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByProductGroup, value: "ProductGroupName"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByCategory, value: "CategoryName"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByRentalStartDate, value: "RentalStartDate"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByRentalEndDate, value: "RentalEndDate"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByIdle, value: "IdleDays"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByAlerts, value: "AlertsCount"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByBattery, value: "BatteryLevel"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByFuel, value: "FluidLevel"},
    ];

    const filterByStatusItems: IDropdownItem[] = [
        {name: Localizer.constructionSiteDetailsEquipmentTabStatusInRent, value: RentaEasyConstants.ConstructionSiteEquipmentConstants.statusInRent},
        {name: Localizer.constructionSiteDetailsEquipmentTabStatusReturned, value: RentaEasyConstants.ConstructionSiteEquipmentConstants.statusReturned},
        {name: Localizer.constructionSiteDetailsEquipmentTabStatusReturnRequested, value: RentaEasyConstants.ConstructionSiteEquipmentConstants.statusReturnRequested},
    ];

    const onFiltersReset = () => {
        onParamValueChange({deviceNames: [], productGroups: [], statuses: []});
    };

    const toggleDeviceNamesItem = (label: string) => {
        if (params.deviceNames.some((name) => name === label)) {
            onParamValueChange({deviceNames: params.deviceNames.filter((name) => name !== label)});
        } else {
            onParamValueChange({deviceNames: [...params.deviceNames, label]});
        }
    };

    const toggleProductGroupFilterItem = (label: string, id?: string) => {
        if (params.productGroups.some((group) => group.name === label)) {
            onParamValueChange({productGroups: params.productGroups.filter((group) => group.name !== label)});
        } else {
            onParamValueChange({productGroups: [...params.productGroups, {id: id, name: label}]});
        }
    };

    const toggleStatusFilterItem = (label: string, id?: string) => {
        if (params.statuses.some((status) => status.name === label)) {
            onParamValueChange({statuses: params.statuses.filter((status) => status.name !== label)});
        } else {
            onParamValueChange({statuses: [...params.statuses, {id: id, name: label}]});
        }
    };

    const toggleFilterItem = (tag: Tag) => {
        const tagType = tag.type;
        const tagId = tag.id;
        const tagName = tag.label;
        switch (tagType) {
            case FilterType.DEVICE_NAMES:
                toggleDeviceNamesItem(tagName);
                break;
            case FilterType.PRODUCT_GROUPS:
                toggleProductGroupFilterItem(tagName, tagId);
                break;
            case FilterType.STATUSES:
                toggleStatusFilterItem(tagName, tagId);
                break;
            default:
                console.error(`Unknown tag type: ${tagType}`);
        }
    };

    const loadPopularProductGroups = async () => {
        try {
            const response = await getMostPopularSearchesAsync(SearchType.ProductGroup);
            setPopularProductGroups(response.map((group) => ({name: group.name, value: group.externalId})));
            setProductGroupNames(response);
        } catch (error) {
            console.error("Error loading popular product groups:", error);
        }
    };

    const loadProductGroupSuggestions = async (searchTerm: string) : Promise<IDropdownItem[]> => {
        try {
            const response = await searchProductGroupNames(searchTerm);
            setProductGroupNames(response);
            return (response ?? []).map((group) => (
                {name: group.name, value: group.externalId}
            ));
        } catch (error) {
            console.error("Error loading product group suggestions:", error);
            return [];
        }
    }

    const formatSelectedLabel = (selectedCount: number) => {
        return selectedCount ? Localizer.get(Localizer.componentDropdownMultipleSelectedLanguageItemName, selectedCount) : undefined;
    };

    const getDeviceNameSuggestions = useCallback((value: string) => {
        return value ? [] : deviceNameSuggestions.map(item => ({name: item, value: item}));
    }, [deviceNameSuggestions]);

    const getProductGroupSuggestions = useCallback(async (value: string) : Promise<IDropdownItem[]> => {
        const searchTerm = value.trim();
        if (searchTerm.length >= 3) {
            return await loadProductGroupSuggestions(searchTerm);
        }

        if (value) {
            return [];
        }

        return productGroupSuggestions.length ? productGroupSuggestions.map(item => ({name: item, value: item})) : popularProductGroups;
    }, [productGroupSuggestions, popularProductGroups]);
    
    const setProductGroupsLocal = (item: IDropdownItem) => {
        if (params.productGroups.some((group) => group.id === item.value && group.name === item.name)) {
            return;
        }

        if (item.value) {
            const productGroupName = productGroupNames.find(group => group.externalId === item.value);
            if (productGroupName) {
                saveSearchLogAsync(SearchType.ProductGroup, productGroupName.id).catch();
            }
        }

        onParamValueChange({productGroups: [...params.productGroups, {id: item.value ? item.value.toString() : undefined, name: item.name}]});
    };

    const handleProductGroupSuggestionClicked = (item: IDropdownItem) => {
        setProductGroup('');
        let selectedItem = item;
        if (item.name === item.value) {
            selectedItem = {name: item.name};
        }

        setProductGroupsLocal(selectedItem);
    };

    const handleFormSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        const trimmedDeviceName = deviceName.trim();
        if (trimmedDeviceName) {
            setDeviceNameSuggestions(prev => [trimmedDeviceName, ...prev.filter(item => item !== trimmedDeviceName)].slice(0, 5));
            onParamValueChange({deviceNames: [...(new Set([...params.deviceNames, trimmedDeviceName]))]});
            setDeviceName('');
        }

        const trimmedProductGroup = productGroup.trim();
        if (trimmedProductGroup) {
            setProductGroupSuggestions(prev => [trimmedProductGroup, ...prev.filter(item => item !== trimmedProductGroup)].slice(0, 5));
            setProductGroupsLocal({value: undefined, name: trimmedProductGroup});
            setProductGroup('');
        }
        (document.activeElement as HTMLInputElement)?.blur();
    };

    return (
        <div className={styles.container}>
            <form className={styles.filtersContainer} onSubmit={handleFormSubmit}>
                <div className={styles.searchRow}>
                    <div className={styles.deviceName}>
                        <Input id="filter-equipments-by-name"
                               value={deviceName}
                               label={Localizer.fleetMonitoringPageFiltersLabelsName}
                               inputProperties={{
                                   name: 'deviceName',
                                   placeholder: formatSelectedLabel(params.deviceNames.length),
                                   autoComplete: 'off'
                               }}
                               onChange={setDeviceName}
                               useSuggestions
                               minValueLengthForSuggestions={0}
                               getSuggestionsDelayMs={0}
                               getSuggestions={(value: string) => getDeviceNameSuggestions(value)}
                               onSuggestionClick={(item: IDropdownItem) => {
                                   setDeviceName('');
                                   onParamValueChange({deviceNames: [...(new Set([...params.deviceNames, item.value!.toString()]))]});
                               }}
                        />
                    </div>
                    <div className={styles.productGroup}>
                        <Input id="filter-equipments-by-product-group"
                               value={productGroup}
                               label={Localizer.constructionSiteDetailsEquipmentTabProductGroup}
                               inputProperties={{
                                   name: 'productGroup',
                                   placeholder: formatSelectedLabel(params.productGroups.length),
                                   autoComplete: 'off'
                               }}
                               onChange={setProductGroup}
                               useSuggestions
                               minValueLengthForSuggestions={0}
                               getSuggestionsDelayMs={productGroupsSuggestionsDelayMs}
                               getSuggestions={async (value: string) => await getProductGroupSuggestions(value)}
                               onSuggestionClick={handleProductGroupSuggestionClicked}
                               data-cy="filter-equipments-by-product-group"
                        />
                    </div>
                    <div className={styles.search}>
                        {isMobile ? (
                            <Button className={styles.buttonWithIcon}
                                    type={ButtonType.Secondary}
                                    size={ButtonSize.Medium}
                                    id="toggle-additional-filters-button"
                                    iconLeft={{name: "sliders", color: "#FE5000", size: IconSize.Medium}}
                                    onClick={() => setAdditionalFiltersVisible(prev => !prev)}
                            >
                                {Localizer.constructionSiteDetailsEquipmentTabMoreFilters}
                            </Button>
                        ) : (
                            <Button className={styles.iconButton}
                                    type={ButtonType.Icon}
                                    size={ButtonSize.Medium}
                                    id="toggle-additional-filters-button"
                                    onClick={() => setAdditionalFiltersVisible(prev => !prev)}
                            >
                                <Icon name="sliders" color="#FE5000" size={IconSize.Medium}/>
                            </Button>
                        )}
                        <Button className={styles.buttonWithIcon}
                                type={ButtonType.Primary}
                                size={ButtonSize.Medium}
                                id="search-equipments-button"
                                iconLeft={{name: "magnifyingGlass", color: "white", size: IconSize.Medium}}
                                submit
                        >
                            {Localizer.searchTitle}
                        </Button>
                    </div>
                </div>
                {additionalFiltersVisible ? (
                    <div className={styles.additionalFiltersContainer} data-cy="additional-filters-container">
                        <Dropdown id="filter-equipments-by-statuses"
                                  className={styles.formField}
                                  items={filterByStatusItems}
                                  label={Localizer.constructionSiteDetailsEquipmentTabStatus}
                                  selectedItem={statuses}
                                  onChange={(items: IDropdownItem[]) => onParamValueChange({statuses: items.map((item) => ({id: item.value!.toString(), name: item.name}))})}
                                  multiSelect
                                  allowEmpty
                                  formatSelectedLabel={formatSelectedLabel}
                        />
                    </div>
                ) : null}
            </form>
            {hasTags && (
                <div className={styles.filterTags}>
                    {combinedFilterValues.length ? (
                        <Tags className={styles.tagsWrapper}
                              items={combinedFilterValues}
                              onClick={(tag: Tag) => toggleFilterItem(tag)}
                              onDelete={(tag: Tag) => toggleFilterItem(tag)}
                              onDeleteAll={onFiltersReset}
                              localizations={{deleteAllTagsTitle: Localizer.fleetMonitoringPageFiltersTagsRemoveAll}}
                        />
                    ) : null}
                </div>
            )}
            <div className={styles.sortRow}>
                <Dropdown id="equipments-sort-by"
                          items={sortByItems}
                          selectedItem={params.sortBy}
                          className={styles.formField}
                          label={Localizer.fleetMonitoringPageFiltersLabelsSortBy}
                          onChange={(item: IDropdownItem) => onParamValueChange({sortBy: item.value!.toString()})}
                />
                <Dropdown id="equipments-sort-order"
                          items={sortOrderItems}
                          selectedItem={params.sortOrder}
                          className={styles.formField}
                          label={Localizer.fleetMonitoringPageFiltersLabelsSortOrder}
                          onChange={(item: IDropdownItem) => onParamValueChange({sortOrder: item.value!.toString()})}
                />
                <div className={styles.buttonContainer}>
                    <Button className={styles.getReportButton}
                            type={ButtonType.Secondary}
                            size={ButtonSize.Medium}
                            id={"getReportButton"}
                            iconLeft={{name: "download", color: "#FE5000", size: IconSize.Medium}}
                            onClick={onGetReportModalOpen}
                    >
                        {Localizer.constructionSiteDetailsEquipmentTabGetReportButton}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FiltersAndSort;